/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtomSetter,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { AppBar, Toolbar, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Platform } from 'componentregistry/platform/Platform';
import { activeItemSelector } from 'designer/activeItemSelector';
import useWindowSize from 'hooks/useWindowSize';
import OrphanItems from 'layout/Sidebar/panels/TreeView/OrphanItems';
import SelectedItemToolbar from 'layout/Sidebar/panels/TreeView/SelectedItemToolbar';
import { TreeType } from 'layout/Sidebar/panels/TreeView/state';
import ErrorBoundary from 'platform/ErrorBoundary';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useRef } from 'react';
import useUser from 'root/useUser';
import {
  activePinnedSidebarPanelSelector,
  activeSidebarPanelAtom,
  contextMenuAtom,
  getDataStoreAlias,
} from '../../atoms';
import Clipboard from './Clipboard';
import Tree from './Tree';
import TreeViewHeader from './TreeViewHeader';

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    margin: 0,
    paddingLeft: 0,
    paddingTop: 8,
    overflow: 'auto',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    '--spacing': '26px',
    '--radius': '8px',
    '& ul li': {
      borderLeft: `1px solid ${
        theme.palette.mode === 'dark' ? '#333' : '#ddd'
      }`,
    },
    '& ul li:last-child': {
      borderLeftColor: 'transparent',
    },
    '& ul': {
      marginLeft: 'calc(var(--radius) - var(--spacing))',
      paddingLeft: 0,
    },
    '& li': {
      display: 'block',
      position: 'relative',
      paddingLeft: 'calc(2 * var(--spacing) - var(--radius))',
    },
    '& ul li:before': {
      content: "''",
      display: 'block',
      position: 'absolute',
      top: 'calc(var(--spacing) / -2)',
      left: '-0.5px',
      width: 'calc(var(--spacing))',
      height: 'calc(var(--spacing))',
      border: `solid ${theme.palette.mode === 'dark' ? '#333' : '#ddd'}`,
      borderWidth: '0 0 1px 1px',
    },
  },
  header: {
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  paddingZero: {
    padding: 0,
    margin: 0,
  },
}));

type TreeViewProps = {
  treename: TreeType;
  noHeader: boolean;
  headerClassName?: string;
  height: number;
  width?: number;
  platform: Platform;
  pageUid: string;
  appUid: string;
};

export function TreeView({
  treename,
  noHeader = false,
  headerClassName,
  height,
  width,
  platform,
  pageUid,
  appUid,
}: TreeViewProps): React.ReactElement {
  const { userName } = useUser();
  const ref = useRef<HTMLUListElement | null>(null);
  const classes = useStyles();
  const setPoint = useStateXAtomSetter(contextMenuAtom);
  const setActiveSidebarPanel = useStateXAtomSetter(activeSidebarPanelAtom);
  const get = useStateXGetter();
  const set = useStateXSetter();

  const onClick = (
    e: React.SyntheticEvent<HTMLElement, MouseEvent>,
    itemId: string,
  ) => {
    e.stopPropagation();
    set(
      activeItemSelector,
      {
        appUid,
        pageId: pageUid,
        itemId,
        expand: false,
        treename,
        event: e.nativeEvent,
      },
      { params: { appUid, pageId: pageUid, treename }, props: platform },
    );
  };

  const designer = treename === 'designer';
  const onDoubleClick = designer
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>, itemId: string) => {
        set(
          activeItemSelector,
          { appUid, pageId: pageUid, itemId, expand: false, treename },
          { params: { appUid, pageId: pageUid, treename }, props: platform },
        );
        if (
          get(activePinnedSidebarPanelSelector, {
            params: { userName },
          }) !== 'props'
        ) {
          setActiveSidebarPanel('props');
        }
      }
    : undefined;

  const onContextMenu = designer
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>, itemId: string) => {
        e.stopPropagation();
        e.preventDefault();
        set(
          activeItemSelector,
          { appUid, pageId: pageUid, itemId, expand: false, treename },
          { params: { appUid, pageId: pageUid, treename }, props: platform },
        );
        const item = platform.getItem(appUid, pageUid, itemId);
        const dsAlias = item ? getDataStoreAlias(item) : '';
        setPoint({
          appUid,
          pageId: pageUid,
          itemId,
          left: e.clientX,
          top: e.clientY,
          orphan: false,
          dsAlias: dsAlias ?? '',
        });
      }
    : undefined;

  const h = height - (noHeader ? 0 : 40);

  return (
    <>
      {noHeader ? null : (
        <TreeViewHeader
          treename={treename}
          className={headerClassName}
          platform={platform}
          appUid={appUid}
          pageId={pageUid}
        />
      )}
      <ul className={classes.body} ref={ref} style={{ height: h, width }}>
        {pageUid ? (
          <Tree
            rootref={ref}
            pageId={pageUid}
            itemId="root"
            first
            last
            root
            orphan={false}
            level={0}
            treename={treename}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            platform={platform}
            appUid={appUid}
          />
        ) : null}
        <ErrorBoundary platform={platform}>
          {designer ? <OrphanItems appUid={appUid} pageId={pageUid} /> : null}
        </ErrorBoundary>
      </ul>
    </>
  );
}

export function TreeViewPanel({
  appUid,
  pageUid,
  pinned,
}: {
  appUid: string;
  pageUid: string;
  pinned: boolean;
}) {
  const classes = useStyles();
  const [, wh] = useWindowSize();
  const platform = usePlatform();
  return (
    <>
      <AppBar
        className={classes.header}
        position="relative"
        color="transparent"
      >
        <Toolbar className={classes.paddingZero}>
          <TreeViewHeader
            treename="designer"
            platform={platform}
            appUid={appUid}
            pageId={pageUid}
          />
        </Toolbar>
      </AppBar>
      <SelectedItemToolbar appUid={appUid} pageId={pageUid} />
      <Clipboard appUid={appUid} pageId={pageUid} />
      <TreeView
        treename="designer"
        noHeader
        height={wh - 64}
        platform={platform}
        appUid={appUid}
        pageUid={pageUid}
      />
    </>
  );
}

export default memo(TreeView);
